import { useEffect, useRef, useState } from "react";

import { NO_PICK, type AskQuestion, type Pick } from "../askUser";

/** A single choice answers its question: the next one slides in once the pick has shown. */
const NEXT_AFTER_MS = 360;

interface Picking {
  /** The pause these picks answer: another one starts over. */
  key: string | null;
  page: number;
  /** The last move went back a question: the pane slides in from the left. */
  back: boolean;
  picks: Pick[];
}

// a question with no options (a run paused before ask_user had them) takes words straight away
const start = (questions: AskQuestion[], key: string | null): Picking => ({
  key,
  page: 0,
  back: false,
  picks: questions.map((q) => (q.options.length ? NO_PICK : { options: [], own: "" })),
});

export type QuestionPicks = ReturnType<typeof useQuestionPicks>;

/** The person's picks for the questions a run waits on, one question at a time. */
export default function useQuestionPicks(questions: AskQuestion[], key: string | null) {
  const [state, setState] = useState(() => start(questions, key));
  const advance = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(advance.current), []);
  const current = state.key === key ? state : start(questions, key);
  if (current !== state) setState(current);

  const go = (page: number) => {
    window.clearTimeout(advance.current);
    setState((s) => ({ ...s, page, back: page < s.page }));
  };
  // what the person does next wins over the move a pick queued
  const update = (change: (pick: Pick, question: AskQuestion) => Pick) => {
    window.clearTimeout(advance.current);
    setState((s) => ({ ...s, picks: s.picks.map((pick, i) => (i === s.page ? change(pick, questions[i]) : pick)) }));
  };
  /** Option `i` picked: on a multiple choice it toggles; on a single one it is the answer,
      and the next question follows. */
  const choose = (i: number) => {
    const question = questions[current.page];
    if (question.multi_select) {
      update((pick) => ({ ...pick, options: pick.options.includes(i) ? pick.options.filter((o) => o !== i) : [...pick.options, i].sort((a, b) => a - b) }));
      return;
    }
    update(() => ({ options: [i], own: null }));
    const next = current.page + 1;
    if (next < questions.length) {
      // only for this pause: another one may have come in meanwhile
      advance.current = window.setTimeout(() => setState((s) => (s.key === key ? { ...s, page: next, back: false } : s)), NEXT_AFTER_MS);
    }
  };

  /** "Something else": the person's own words take a single choice's place, or join a
      multiple choice's picks, where choosing it again drops them. */
  const chooseOwn = () =>
    update((pick, question) => {
      if (pick.own !== null) return question.multi_select ? { ...pick, own: null } : pick;
      return { options: question.multi_select ? pick.options : [], own: "" };
    });

  const writeOwn = (own: string) => update((pick) => ({ ...pick, own }));

  return { page: current.page, back: current.back, picks: current.picks, choose, chooseOwn, writeOwn, go };
}
